import React, { useEffect, useState } from 'react'

// Types
type Offer = { id: string; name: string; url: string }
type LandingPage = { id: string; name: string; url: string; description?: string }
type CustomEvent = { id: string; name: string; description?: string }
type FlowVariant = { landing_page_id?: string; offer_id: string; weight: number }
type Flow = { id: string; name: string; use_landing_pages?: boolean; variants: FlowVariant[] }
type Campaign = { id: string; host: string; name: string; flow_id: string; active: boolean; traffic_source_id?: string }
type TrafficSourceParam = { name: string; param_name: string; target_field: string; macro: string }
type TrafficSource = { id: string; name: string; params: TrafficSourceParam[]; created_at?: string; updated_at?: string }

const API = import.meta.env.VITE_ADMIN_BASE as string
const TOKEN = import.meta.env.VITE_ADMIN_TOKEN as string | undefined

async function api(path: string, init: RequestInit = {}) {
  if (!API) throw new Error('VITE_ADMIN_BASE is not set')
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (TOKEN) headers.authorization = `Bearer ${TOKEN}`
  const res = await fetch(`${API}${path}`, { ...init, headers: { ...headers, ...(init.headers || {}) } })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return res
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

type TopTab = 'stats' | 'admin'
type StatsTab = 'campaigns' | 'offers' | 'flows' | 'landing-pages'
type AdminTab = 'campaigns' | 'flows' | 'offers' | 'landing-pages' | 'events' | 'traffic-sources'

export default function App() {
  const [topTab, setTopTab] = useState<TopTab>(() => {
    const saved = localStorage.getItem('topTab')
    return (saved as TopTab) || 'stats'
  })
  const [statsTab, setStatsTab] = useState<StatsTab>(() => {
    const saved = localStorage.getItem('statsTab')
    return (saved as StatsTab) || 'campaigns'
  })
  const [adminTab, setAdminTab] = useState<AdminTab>(() => {
    const saved = localStorage.getItem('adminTab')
    return (saved as AdminTab) || 'campaigns'
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    localStorage.setItem('topTab', topTab)
  }, [topTab])

  useEffect(() => {
    localStorage.setItem('statsTab', statsTab)
  }, [statsTab])

  useEffect(() => {
    localStorage.setItem('adminTab', adminTab)
  }, [adminTab])

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      padding: '24px 24px 60px 24px'
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          borderLeft: '4px solid #2563eb'
        }}>
          <h1 style={{
            marginTop: 0,
            marginBottom: 8,
            color: '#1e293b',
            fontSize: 28,
            fontWeight: 700
          }}>
            Flow Tracker
          </h1>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            {API ? `Connected to ${API}` : 'API not configured'}
          </div>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            border: '1px solid #fecaca',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#991b1b',
                padding: '6px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Top-level tabs */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          background: 'white',
          borderRadius: 8,
          padding: 6,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
        }}>
          <TabButton active={topTab === 'stats'} onClick={() => setTopTab('stats')}>Stats</TabButton>
          <TabButton active={topTab === 'admin'} onClick={() => setTopTab('admin')}>Admin</TabButton>
        </div>

        {/* Stats Section */}
        {topTab === 'stats' && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <SubTabButton active={statsTab === 'campaigns'} onClick={() => setStatsTab('campaigns')}>🎯 Campaigns</SubTabButton>
              <SubTabButton active={statsTab === 'offers'} onClick={() => setStatsTab('offers')}>🎁 Offers</SubTabButton>
              <SubTabButton active={statsTab === 'flows'} onClick={() => setStatsTab('flows')}>🌊 Flows</SubTabButton>
              <SubTabButton active={statsTab === 'landing-pages'} onClick={() => setStatsTab('landing-pages')}>📄 Landing Pages</SubTabButton>
            </div>

            {statsTab === 'campaigns' && <StatsCampaignsTab setError={setError} />}
            {statsTab === 'offers' && <StatsOffersTab setError={setError} />}
            {statsTab === 'flows' && <StatsFlowsTab setError={setError} />}
            {statsTab === 'landing-pages' && <StatsLandingPagesTab setError={setError} />}
          </div>
        )}

        {/* Admin Section */}
        {topTab === 'admin' && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <SubTabButton active={adminTab === 'campaigns'} onClick={() => setAdminTab('campaigns')}>🎯 Campaigns</SubTabButton>
              <SubTabButton active={adminTab === 'flows'} onClick={() => setAdminTab('flows')}>🌊 Flows</SubTabButton>
              <SubTabButton active={adminTab === 'offers'} onClick={() => setAdminTab('offers')}>🎁 Offers</SubTabButton>
              <SubTabButton active={adminTab === 'landing-pages'} onClick={() => setAdminTab('landing-pages')}>📄 Landing Pages</SubTabButton>
              <SubTabButton active={adminTab === 'events'} onClick={() => setAdminTab('events')}>📊 Events</SubTabButton>
              <SubTabButton active={adminTab === 'traffic-sources'} onClick={() => setAdminTab('traffic-sources')}>🚦 Traffic Sources</SubTabButton>
            </div>

            {adminTab === 'campaigns' && <CampaignsTab setError={setError} />}
            {adminTab === 'flows' && <FlowsTab setError={setError} />}
            {adminTab === 'offers' && <OffersTab setError={setError} />}
            {adminTab === 'landing-pages' && <LandingPagesTab setError={setError} />}
            {adminTab === 'events' && <CustomEventsTab setError={setError} />}
            {adminTab === 'traffic-sources' && <TrafficSourcesTab setError={setError} />}
          </div>
        )}

        <div style={{
          marginTop: 32,
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: 12
        }}>
          Flow Tracker Panel · {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '14px 24px',
        border: 'none',
        background: active ? '#2563eb' : 'transparent',
        color: active ? '#fff' : '#64748b',
        borderRadius: 8,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 15,
        boxShadow: active ? '0 1px 3px rgba(37, 99, 235, 0.3)' : 'none',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = '#f1f5f9'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
        }
      }}
    >
      {children}
    </button>
  )
}

function SubTabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 20px',
        border: active ? 'none' : '1px solid #e5e7eb',
        background: active ? '#2563eb' : 'white',
        color: active ? '#fff' : '#64748b',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        boxShadow: active ? '0 1px 3px rgba(37, 99, 235, 0.2)' : 'none',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = '#f8fafc'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'white'
        }
      }}
    >
      {children}
    </button>
  )
}

// ===== STATS: CAMPAIGNS TAB =====
type CampaignStats = {
  campaign_id: string
  campaign_name: string
  campaign_host: string
  total_clicks: number
  unique_visitors: number
  last_click: string
}

function StatsCampaignsTab({ setError }: { setError: (e: string | null) => void }) {
  const [stats, setStats] = useState<CampaignStats[]>([])
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Campaign | null>(null)
  const [flows, setFlows] = useState<Flow[]>([])
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [availableEvents, setAvailableEvents] = useState<string[]>([])
  const [selectedEvents, setSelectedEvents] = useState<string[]>(() => {
    const saved = localStorage.getItem('selectedEvents_campaigns')
    return saved ? JSON.parse(saved) : []
  })
  const [showEventSelector, setShowEventSelector] = useState(false)

  useEffect(() => {
    localStorage.setItem('selectedEvents_campaigns', JSON.stringify(selectedEvents))
  }, [selectedEvents])

  async function loadStats() {
    try {
      setLoading(true); setError(null)
      const res = await api('/stats')
      const data = await res.json()
      setStats(data.campaigns || data) // Handle both old and new format
      if (data.available_events) {
        setAvailableEvents(data.available_events)
      }
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function loadFlows() {
    try {
      const res = await api('/flows')
      setFlows(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function loadCampaignForEdit(campaignId: string, campaignHost: string) {
    try {
      setError(null)
      const res = await api('/campaigns')
      const campaigns: Campaign[] = await res.json()
      const campaign = campaigns.find(c => c.id === campaignId && c.host === campaignHost)
      if (campaign) {
        setEditing(campaign)
      } else {
        setError('Campaign not found')
      }
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function saveCampaign() {
    if (!editing) return
    try {
      setError(null)
      if (!editing.host || !editing.flow_id) {
        throw new Error('Host and Flow are required')
      }
      await api(`/campaigns/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await loadStats()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function copyLink(stat: CampaignStats) {
    let url = `https://${stat.campaign_host}/${stat.campaign_id}`

    try {
      // Load full campaign to check for traffic source
      const campaignsRes = await api('/campaigns')
      const campaigns: Campaign[] = await campaignsRes.json()
      const campaign = campaigns.find(c => c.id === stat.campaign_id && c.host === stat.campaign_host)

      // Add traffic source parameters if configured
      if (campaign && campaign.traffic_source_id) {
        const tsRes = await api('/traffic-sources')
        const trafficSources: TrafficSource[] = await tsRes.json()
        const ts = trafficSources.find(t => t.id === campaign.traffic_source_id)
        if (ts && ts.params && ts.params.length > 0) {
          const macroParams = ts.params.map(p => `${p.param_name}=${p.macro}`).join('&')
          url = `${url}?${macroParams}`
        }
      }

      await navigator.clipboard.writeText(url)
      setCopiedId(stat.campaign_id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (e) {
      setError('Failed to copy to clipboard')
    }
  }

  useEffect(() => { loadStats(); loadFlows() }, [])

  if (selectedCampaign) {
    return <CampaignDetailView campaignId={selectedCampaign} onBack={() => setSelectedCampaign(null)} setError={setError} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Campaign Statistics</h2>
        <button onClick={loadStats} style={styles.btn}>Refresh</button>
      </div>

      {availableEvents.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <button
            onClick={() => setShowEventSelector(!showEventSelector)}
            style={{...styles.btn, display: 'flex', alignItems: 'center', gap: 8}}
          >
            Event Columns ({selectedEvents.length}) {showEventSelector ? '▲' : '▼'}
          </button>
          {showEventSelector && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: 200,
              maxHeight: 300,
              overflowY: 'auto'
            }}>
              {availableEvents.map(event => (
                <label key={event} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEvents([...selectedEvents, event])
                      } else {
                        setSelectedEvents(selectedEvents.filter(ev => ev !== event))
                      }
                    }}
                  />
                  <span style={{ fontSize: 13 }}>{event}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <div>Loading...</div>}

      {stats.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No data yet. Create campaigns and start tracking clicks!
        </div>
      )}

      {stats.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{...styles.table, tableLayout: 'fixed'}}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={{...styles.tableHeader, width: 250, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Campaign Name</th>
                <th style={{...styles.tableHeader, width: 250, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Campaign ID</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Visits</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Unique</th>
                {selectedEvents.map(event => (
                  <th key={event} style={{...styles.tableHeader, width: 100, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>{event}</th>
                ))}
                <th style={{...styles.tableHeader, width: 200, minWidth: 150}}>Last Click</th>
                <th style={{...styles.tableHeader, width: 100, borderRight: 'none'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.map(stat => (
                <tr
                  key={stat.campaign_id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white'
                  }}
                >
                  <td
                    style={{...styles.tableCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedCampaign(stat.campaign_id)}
                  >
                    {stat.campaign_name}
                  </td>
                  <td
                    style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedCampaign(stat.campaign_id)}
                  >
                    {stat.campaign_id}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', fontWeight: 'bold', cursor: 'pointer'}}
                    onClick={() => setSelectedCampaign(stat.campaign_id)}
                  >
                    {stat.total_clicks.toLocaleString()}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                    onClick={() => setSelectedCampaign(stat.campaign_id)}
                  >
                    {stat.unique_visitors.toLocaleString()}
                  </td>
                  {selectedEvents.map(event => (
                    <td
                      key={event}
                      style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                      onClick={() => setSelectedCampaign(stat.campaign_id)}
                    >
                      {((stat as any)[`event_${event}`] || 0).toLocaleString()}
                    </td>
                  ))}
                  <td
                    style={{...styles.tableCell, fontSize: 12, color: '#666', cursor: 'pointer'}}
                    onClick={() => setSelectedCampaign(stat.campaign_id)}
                  >
                    {new Date(stat.last_click).toLocaleString()}
                  </td>
                  <td style={{...styles.tableCell, borderRight: 'none'}}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          loadCampaignForEdit(stat.campaign_id, stat.campaign_host)
                        }}
                        style={styles.iconBtn}
                        title="Edit campaign"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          copyLink(stat)
                        }}
                        style={{
                          ...styles.iconBtn,
                          background: copiedId === stat.campaign_id ? '#dcfce7' : 'transparent'
                        }}
                        title="Copy campaign link"
                      >
                        {copiedId === stat.campaign_id ? '✓' : '🔗'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>Edit Campaign</h3>

            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Campaign UUID (immutable):</div>
              <code style={{ fontSize: 13 }}>{editing.id}</code>
            </div>

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Summer 2025 Campaign"
              />
            </label>
            <label style={styles.label}>
              Host *<br/>
              <input
                style={styles.input}
                value={editing.host}
                onChange={(e) => setEditing({ ...editing, host: e.target.value })}
                placeholder="trk.flow-trk.com"
              />
            </label>
            <label style={styles.label}>
              Flow *<br/>
              <select
                style={styles.input}
                value={editing.flow_id}
                onChange={(e) => setEditing({ ...editing, flow_id: e.target.value })}
              >
                <option value="">Select flow...</option>
                {flows.map(f => <option key={f.id} value={f.id}>{f.name || f.id}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              Active
            </label>

            {editing.host && (
              <div style={{ marginTop: 16, padding: 12, background: '#e8f5e9', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Tracking URL:</div>
                <code style={{ fontSize: 14 }}>https://{editing.host}/{editing.id}</code>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={saveCampaign} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Campaign Drill-Down View
type CampaignDetail = {
  campaign_id: string
  summary: {
    total_clicks: number
    unique_visitors: number
    first_click: string
    last_click: string
  }
  dimension: string
  breakdown: Array<{ dimension_value: string; clicks: number; unique_visitors: number }>
}

function CampaignDetailView({ campaignId, onBack, setError }: { campaignId: string; onBack: () => void; setError: (e: string | null) => void }) {
  const [dimension, setDimension] = useState('offer')
  const [detail, setDetail] = useState<CampaignDetail | null>(null)
  const [loading, setLoading] = useState(false)

  async function loadDetail() {
    try {
      setLoading(true); setError(null)
      const res = await api(`/stats/campaign/${encodeURIComponent(campaignId)}?dimension=${dimension}`)
      setDetail(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadDetail() }, [dimension])

  const dimensions = [
    { value: 'offer', label: 'Offer' },
    { value: 'flow', label: 'Flow' },
    { value: 'landing_page', label: 'Landing Page' },
    { value: 'event_type', label: 'Event Type' },
    { value: 'country', label: 'Country' },
    { value: 'city', label: 'City' },
    { value: 'region', label: 'Region' },
    { value: 'isp', label: 'ISP' },
    { value: 'colo', label: 'Colo' },
    { value: 'day', label: 'Day' },
    { value: 'hour', label: 'Hour' },
    { value: 'referer', label: 'Referrer' }
  ]

  return (
    <div>
      <button onClick={onBack} style={{ ...styles.btn, marginBottom: 16 }}>← Back to Campaign List</button>

      <h2 style={{ margin: '0 0 16px 0' }}>{campaignId}</h2>

      {detail && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Total Clicks</div>
            <div style={styles.summaryValue}>{detail.summary.total_clicks?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Unique Visitors</div>
            <div style={styles.summaryValue}>{detail.summary.unique_visitors?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>First Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.first_click ? new Date(detail.summary.first_click).toLocaleDateString() : 'N/A'}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Last Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.last_click ? new Date(detail.summary.last_click).toLocaleDateString() : 'N/A'}</div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, marginRight: 8 }}>Break down by:</label>
        <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={{ ...styles.input, width: 'auto' }}>
          {dimensions.map(d => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>

      {loading && <div>Loading...</div>}

      {detail && detail.breakdown.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>{dimensions.find(d => d.value === dimension)?.label}</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Clicks</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Unique Visitors</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>%</th>
            </tr>
          </thead>
          <tbody>
            {detail.breakdown.map((row, i) => {
              const percentage = detail.summary.total_clicks > 0
                ? ((row.clicks / detail.summary.total_clicks) * 100).toFixed(1)
                : '0.0'
              return (
                <tr key={i} style={styles.tableRow}>
                  <td style={styles.tableCell}>{row.dimension_value || '(empty)'}</td>
                  <td style={{...styles.tableCell, textAlign: 'right'}}>{row.clicks.toLocaleString()}</td>
                  <td style={{...styles.tableCell, textAlign: 'right'}}>{row.unique_visitors.toLocaleString()}</td>
                  <td style={{...styles.tableCell, textAlign: 'right', fontWeight: 'bold'}}>{percentage}%</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {detail && detail.breakdown.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No data for this dimension
        </div>
      )}
    </div>
  )
}

// ===== STATS: OFFERS TAB =====
type OfferStats = {
  offer_id: string
  offer_name: string
  total_clicks: number
  unique_visitors: number
  last_click: string
}

function StatsOffersTab({ setError }: { setError: (e: string | null) => void }) {
  const [stats, setStats] = useState<OfferStats[]>([])
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Offer | null>(null)
  const [availableEvents, setAvailableEvents] = useState<string[]>([])
  const [selectedEvents, setSelectedEvents] = useState<string[]>(() => {
    const saved = localStorage.getItem('selectedEvents_offers')
    return saved ? JSON.parse(saved) : []
  })
  const [showEventSelector, setShowEventSelector] = useState(false)

  useEffect(() => {
    localStorage.setItem('selectedEvents_offers', JSON.stringify(selectedEvents))
  }, [selectedEvents])

  async function loadStats() {
    try {
      setLoading(true); setError(null)
      const res = await api('/stats/offers')
      const data = await res.json()
      setStats(data.offers || data)
      if (data.available_events) {
        setAvailableEvents(data.available_events)
      }
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function loadOfferForEdit(offerId: string) {
    try {
      setError(null)
      const res = await api('/offers')
      const offers: Offer[] = await res.json()
      const offer = offers.find(o => o.id === offerId)
      if (offer) {
        setEditing(offer)
      } else {
        setError('Offer not found')
      }
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function saveOffer() {
    if (!editing) return
    try {
      setError(null)
      if (!editing.url) throw new Error('URL is required')
      await api(`/offers/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await loadStats()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { loadStats() }, [])

  if (selectedOffer) {
    return <OfferDetailView offerId={selectedOffer} onBack={() => setSelectedOffer(null)} setError={setError} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Offer Statistics</h2>
        <button onClick={loadStats} style={styles.btn}>Refresh</button>
      </div>

      {availableEvents.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <button
            onClick={() => setShowEventSelector(!showEventSelector)}
            style={{...styles.btn, display: 'flex', alignItems: 'center', gap: 8}}
          >
            Event Columns ({selectedEvents.length}) {showEventSelector ? '▲' : '▼'}
          </button>
          {showEventSelector && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: 200,
              maxHeight: 300,
              overflowY: 'auto'
            }}>
              {availableEvents.map(event => (
                <label key={event} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEvents([...selectedEvents, event])
                      } else {
                        setSelectedEvents(selectedEvents.filter(ev => ev !== event))
                      }
                    }}
                  />
                  <span style={{ fontSize: 13 }}>{event}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <div>Loading...</div>}

      {stats.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No data yet.
        </div>
      )}

      {stats.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{...styles.table, tableLayout: 'fixed'}}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={{...styles.tableHeader, width: 300, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Offer Name</th>
                <th style={{...styles.tableHeader, width: 250, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Offer ID</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Visits</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Unique</th>
                {selectedEvents.map(event => (
                  <th key={event} style={{...styles.tableHeader, width: 100, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>{event}</th>
                ))}
                <th style={{...styles.tableHeader, width: 200, minWidth: 150}}>Last Click</th>
                <th style={{...styles.tableHeader, width: 80, borderRight: 'none'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.map(stat => (
                <tr
                  key={stat.offer_id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white'
                  }}
                >
                  <td
                    style={{...styles.tableCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedOffer(stat.offer_id)}
                  >
                    {stat.offer_name}
                  </td>
                  <td
                    style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedOffer(stat.offer_id)}
                  >
                    {stat.offer_id}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', fontWeight: 'bold', cursor: 'pointer'}}
                    onClick={() => setSelectedOffer(stat.offer_id)}
                  >
                    {stat.total_clicks.toLocaleString()}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                    onClick={() => setSelectedOffer(stat.offer_id)}
                  >
                    {stat.unique_visitors.toLocaleString()}
                  </td>
                  {selectedEvents.map(event => (
                    <td
                      key={event}
                      style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                      onClick={() => setSelectedOffer(stat.offer_id)}
                    >
                      {((stat as any)[`event_${event}`] || 0).toLocaleString()}
                    </td>
                  ))}
                  <td
                    style={{...styles.tableCell, fontSize: 12, color: '#666', cursor: 'pointer'}}
                    onClick={() => setSelectedOffer(stat.offer_id)}
                  >
                    {new Date(stat.last_click).toLocaleString()}
                  </td>
                  <td style={{...styles.tableCell, borderRight: 'none'}}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        loadOfferForEdit(stat.offer_id)
                      }}
                      style={styles.iconBtn}
                      title="Edit offer"
                    >
                      ✏️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>Edit Offer</h3>

            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Offer UUID (immutable):</div>
              <code style={{ fontSize: 13 }}>{editing.id}</code>
            </div>

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="GitHub Landing Page"
              />
            </label>
            <label style={styles.label}>
              URL * (use {'{clickid}'} macro)<br/>
              <input
                style={styles.input}
                value={editing.url}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                placeholder="https://example.com/lp?clickid={clickid}"
              />
            </label>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
              💡 Tip: Use {'{clickid}'} in your URL to track conversions back to this click
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={saveOffer} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Offer Drill-Down View
type OfferDetail = {
  offer_id: string
  summary: {
    total_clicks: number
    unique_visitors: number
    first_click: string
    last_click: string
  }
  campaigns: Array<{ campaign_id: string; campaign_name: string; clicks: number; unique_visitors: number }>
}

function OfferDetailView({ offerId, onBack, setError }: { offerId: string; onBack: () => void; setError: (e: string | null) => void }) {
  const [detail, setDetail] = useState<OfferDetail | null>(null)
  const [loading, setLoading] = useState(false)

  async function loadDetail() {
    try {
      setLoading(true); setError(null)
      const res = await api(`/stats/offer/${encodeURIComponent(offerId)}`)
      setDetail(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadDetail() }, [])

  return (
    <div>
      <button onClick={onBack} style={{ ...styles.btn, marginBottom: 16 }}>← Back to Offer List</button>

      <h2 style={{ margin: '0 0 16px 0' }}>{offerId}</h2>

      {detail && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Total Clicks</div>
            <div style={styles.summaryValue}>{detail.summary.total_clicks?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Unique Visitors</div>
            <div style={styles.summaryValue}>{detail.summary.unique_visitors?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>First Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.first_click ? new Date(detail.summary.first_click).toLocaleDateString() : 'N/A'}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Last Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.last_click ? new Date(detail.summary.last_click).toLocaleDateString() : 'N/A'}</div>
          </div>
        </div>
      )}

      <h3>Campaigns Using This Offer</h3>

      {loading && <div>Loading...</div>}

      {detail && detail.campaigns.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Campaign Name</th>
              <th style={styles.tableHeader}>Campaign ID</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Clicks</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Unique Visitors</th>
            </tr>
          </thead>
          <tbody>
            {detail.campaigns.map((row, i) => (
              <tr key={i} style={styles.tableRow}>
                <td style={styles.tableCell}>{row.campaign_name}</td>
                <td style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12}}>{row.campaign_id}</td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>{row.clicks.toLocaleString()}</td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>{row.unique_visitors.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && detail.campaigns.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No campaigns using this offer yet
        </div>
      )}
    </div>
  )
}

// ===== STATS: FLOWS TAB =====
type FlowStats = {
  flow_id: string
  flow_name: string
  total_clicks: number
  unique_visitors: number
  last_click: string
}

function StatsFlowsTab({ setError }: { setError: (e: string | null) => void }) {
  const [stats, setStats] = useState<FlowStats[]>([])
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Flow | null>(null)
  const [offers, setOffers] = useState<Offer[]>([])
  const [availableEvents, setAvailableEvents] = useState<string[]>([])
  const [selectedEvents, setSelectedEvents] = useState<string[]>(() => {
    const saved = localStorage.getItem('selectedEvents_flows')
    return saved ? JSON.parse(saved) : []
  })
  const [showEventSelector, setShowEventSelector] = useState(false)

  useEffect(() => {
    localStorage.setItem('selectedEvents_flows', JSON.stringify(selectedEvents))
  }, [selectedEvents])

  async function loadStats() {
    try {
      setLoading(true); setError(null)
      const res = await api('/stats/flows')
      const data = await res.json()
      setStats(data.flows || data)
      if (data.available_events) {
        setAvailableEvents(data.available_events)
      }
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function loadOffers() {
    try {
      const res = await api('/offers')
      setOffers(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function loadFlowForEdit(flowId: string) {
    try {
      setError(null)
      const res = await api('/flows')
      const flows: Flow[] = await res.json()
      const flow = flows.find(f => f.id === flowId)
      if (flow) {
        setEditing(flow)
      } else {
        setError('Flow not found')
      }
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function saveFlow() {
    if (!editing) return
    try {
      setError(null)
      if (!editing.variants || editing.variants.length === 0) {
        throw new Error('At least one variant is required')
      }
      await api(`/flows/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await loadStats()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { loadStats(); loadOffers() }, [])

  if (selectedFlow) {
    return <FlowDetailView flowId={selectedFlow} onBack={() => setSelectedFlow(null)} setError={setError} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Flow Statistics</h2>
        <button onClick={loadStats} style={styles.btn}>Refresh</button>
      </div>

      {availableEvents.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <button
            onClick={() => setShowEventSelector(!showEventSelector)}
            style={{...styles.btn, display: 'flex', alignItems: 'center', gap: 8}}
          >
            Event Columns ({selectedEvents.length}) {showEventSelector ? '▲' : '▼'}
          </button>
          {showEventSelector && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: 200,
              maxHeight: 300,
              overflowY: 'auto'
            }}>
              {availableEvents.map(event => (
                <label key={event} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEvents([...selectedEvents, event])
                      } else {
                        setSelectedEvents(selectedEvents.filter(ev => ev !== event))
                      }
                    }}
                  />
                  <span style={{ fontSize: 13 }}>{event}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <div>Loading...</div>}

      {stats.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No data yet.
        </div>
      )}

      {stats.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{...styles.table, tableLayout: 'fixed'}}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={{...styles.tableHeader, width: 300, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Flow Name</th>
                <th style={{...styles.tableHeader, width: 250, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Flow ID</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Visits</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Unique</th>
                {selectedEvents.map(event => (
                  <th key={event} style={{...styles.tableHeader, width: 100, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>{event}</th>
                ))}
                <th style={{...styles.tableHeader, width: 200, minWidth: 150}}>Last Click</th>
                <th style={{...styles.tableHeader, width: 80, borderRight: 'none'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.map(stat => (
                <tr
                  key={stat.flow_id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white'
                  }}
                >
                  <td
                    style={{...styles.tableCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedFlow(stat.flow_id)}
                  >
                    {stat.flow_name}
                  </td>
                  <td
                    style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedFlow(stat.flow_id)}
                  >
                    {stat.flow_id}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', fontWeight: 'bold', cursor: 'pointer'}}
                    onClick={() => setSelectedFlow(stat.flow_id)}
                  >
                    {stat.total_clicks.toLocaleString()}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                    onClick={() => setSelectedFlow(stat.flow_id)}
                  >
                    {stat.unique_visitors.toLocaleString()}
                  </td>
                  {selectedEvents.map(event => (
                    <td
                      key={event}
                      style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                      onClick={() => setSelectedFlow(stat.flow_id)}
                    >
                      {((stat as any)[`event_${event}`] || 0).toLocaleString()}
                    </td>
                  ))}
                  <td
                    style={{...styles.tableCell, fontSize: 12, color: '#666', cursor: 'pointer'}}
                    onClick={() => setSelectedFlow(stat.flow_id)}
                  >
                    {new Date(stat.last_click).toLocaleString()}
                  </td>
                  <td style={{...styles.tableCell, borderRight: 'none'}}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        loadFlowForEdit(stat.flow_id)
                      }}
                      style={styles.iconBtn}
                      title="Edit flow"
                    >
                      ✏️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>Edit Flow</h3>

            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Flow UUID (immutable):</div>
              <code style={{ fontSize: 13 }}>{editing.id}</code>
            </div>

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="My Flow"
              />
            </label>

            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>Variants (Total weight: {editing.variants.reduce((s, v) => s + (v.weight || 0), 0)})</strong>
                <button
                  onClick={() => setEditing({ ...editing, variants: [...editing.variants, { offer_id: '', weight: 100 }] })}
                  style={styles.btnSmall}
                >
                  + Add Variant
                </button>
              </div>
              {editing.variants.map((v, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px', gap: 8, marginBottom: 8 }}>
                  <select
                    style={styles.input}
                    value={v.offer_id}
                    onChange={(e) => {
                      const vs = [...editing.variants]
                      vs[i] = { ...v, offer_id: e.target.value }
                      setEditing({ ...editing, variants: vs })
                    }}
                  >
                    <option value="">Select offer...</option>
                    {offers.map(o => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
                  </select>
                  <input
                    style={styles.input}
                    type="number"
                    value={v.weight}
                    onChange={(e) => {
                      const vs = [...editing.variants]
                      vs[i] = { ...v, weight: Number(e.target.value) }
                      setEditing({ ...editing, variants: vs })
                    }}
                  />
                  <button
                    onClick={() => {
                      const vs = [...editing.variants]
                      vs.splice(i, 1)
                      setEditing({ ...editing, variants: vs })
                    }}
                    style={styles.btnSmall}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={saveFlow} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Flow Drill-Down View
type FlowDetail = {
  flow_id: string
  summary: {
    total_clicks: number
    unique_visitors: number
    first_click: string
    last_click: string
  }
  campaigns: Array<{ campaign_id: string; campaign_name: string; clicks: number; unique_visitors: number }>
}

function FlowDetailView({ flowId, onBack, setError }: { flowId: string; onBack: () => void; setError: (e: string | null) => void }) {
  const [detail, setDetail] = useState<FlowDetail | null>(null)
  const [loading, setLoading] = useState(false)

  async function loadDetail() {
    try {
      setLoading(true); setError(null)
      const res = await api(`/stats/flow/${encodeURIComponent(flowId)}`)
      setDetail(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadDetail() }, [])

  return (
    <div>
      <button onClick={onBack} style={{ ...styles.btn, marginBottom: 16 }}>← Back to Flow List</button>

      <h2 style={{ margin: '0 0 16px 0' }}>{flowId}</h2>

      {detail && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Total Clicks</div>
            <div style={styles.summaryValue}>{detail.summary.total_clicks?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Unique Visitors</div>
            <div style={styles.summaryValue}>{detail.summary.unique_visitors?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>First Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.first_click ? new Date(detail.summary.first_click).toLocaleDateString() : 'N/A'}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Last Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.last_click ? new Date(detail.summary.last_click).toLocaleDateString() : 'N/A'}</div>
          </div>
        </div>
      )}

      <h3>Campaigns Using This Flow</h3>

      {loading && <div>Loading...</div>}

      {detail && detail.campaigns.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Campaign Name</th>
              <th style={styles.tableHeader}>Campaign ID</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Clicks</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Unique Visitors</th>
            </tr>
          </thead>
          <tbody>
            {detail.campaigns.map((row, i) => (
              <tr key={i} style={styles.tableRow}>
                <td style={styles.tableCell}>{row.campaign_name}</td>
                <td style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12}}>{row.campaign_id}</td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>{row.clicks.toLocaleString()}</td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>{row.unique_visitors.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && detail.campaigns.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No campaigns using this flow yet
        </div>
      )}
    </div>
  )
}

// ===== STATS: LANDING PAGES TAB =====
type LandingPageStats = {
  landing_page_id: string
  landing_page_name: string
  total_clicks: number
  unique_visitors: number
  last_click: string
}

function StatsLandingPagesTab({ setError }: { setError: (e: string | null) => void }) {
  const [stats, setStats] = useState<LandingPageStats[]>([])
  const [selectedLP, setSelectedLP] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<LandingPage | null>(null)
  const [availableEvents, setAvailableEvents] = useState<string[]>([])
  const [selectedEvents, setSelectedEvents] = useState<string[]>(() => {
    const saved = localStorage.getItem('selectedEvents_landing_pages')
    return saved ? JSON.parse(saved) : []
  })
  const [showEventSelector, setShowEventSelector] = useState(false)

  useEffect(() => {
    localStorage.setItem('selectedEvents_landing_pages', JSON.stringify(selectedEvents))
  }, [selectedEvents])

  async function loadStats() {
    try {
      setLoading(true); setError(null)
      const res = await api('/stats/landing-pages')
      const data = await res.json()
      setStats(data.landing_pages || data)
      if (data.available_events) {
        setAvailableEvents(data.available_events)
      }
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function loadLPForEdit(lpId: string) {
    try {
      setError(null)
      const res = await api('/landing-pages')
      const landingPages: LandingPage[] = await res.json()
      const lp = landingPages.find(l => l.id === lpId)
      if (lp) {
        setEditing(lp)
      } else {
        setError('Landing Page not found')
      }
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function saveLP() {
    if (!editing) return
    try {
      setError(null)
      if (!editing.name || !editing.url) throw new Error('Name and URL are required')
      await api(`/landing-pages/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await loadStats()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { loadStats() }, [])

  if (selectedLP) {
    return <LandingPageDetailView lpId={selectedLP} onBack={() => setSelectedLP(null)} setError={setError} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Landing Page Statistics</h2>
        <button onClick={loadStats} style={styles.btn}>Refresh</button>
      </div>

      {availableEvents.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <button
            onClick={() => setShowEventSelector(!showEventSelector)}
            style={{...styles.btn, display: 'flex', alignItems: 'center', gap: 8}}
          >
            Event Columns ({selectedEvents.length}) {showEventSelector ? '▲' : '▼'}
          </button>
          {showEventSelector && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: 200,
              maxHeight: 300,
              overflowY: 'auto'
            }}>
              {availableEvents.map(event => (
                <label key={event} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEvents([...selectedEvents, event])
                      } else {
                        setSelectedEvents(selectedEvents.filter(ev => ev !== event))
                      }
                    }}
                  />
                  <span style={{ fontSize: 13 }}>{event}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <div>Loading...</div>}

      {stats.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No data yet.
        </div>
      )}

      {stats.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{...styles.table, tableLayout: 'fixed'}}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={{...styles.tableHeader, width: 300, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>Landing Page Name</th>
                <th style={{...styles.tableHeader, width: 250, minWidth: 150, resize: 'horizontal' as any, overflow: 'hidden'}}>LP ID</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Visits</th>
                <th style={{...styles.tableHeader, width: 120, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>Unique</th>
                {selectedEvents.map(event => (
                  <th key={event} style={{...styles.tableHeader, width: 100, minWidth: 80, textAlign: 'right', resize: 'horizontal' as any, overflow: 'hidden'}}>{event}</th>
                ))}
                <th style={{...styles.tableHeader, width: 200, minWidth: 150}}>Last Click</th>
                <th style={{...styles.tableHeader, width: 80, borderRight: 'none'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.map(stat => (
                <tr
                  key={stat.landing_page_id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white'
                  }}
                >
                  <td
                    style={{...styles.tableCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedLP(stat.landing_page_id)}
                  >
                    {stat.landing_page_name}
                  </td>
                  <td
                    style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                    onClick={() => setSelectedLP(stat.landing_page_id)}
                  >
                    {stat.landing_page_id}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', fontWeight: 'bold', cursor: 'pointer'}}
                    onClick={() => setSelectedLP(stat.landing_page_id)}
                  >
                    {stat.total_clicks.toLocaleString()}
                  </td>
                  <td
                    style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                    onClick={() => setSelectedLP(stat.landing_page_id)}
                  >
                    {stat.unique_visitors.toLocaleString()}
                  </td>
                  {selectedEvents.map(event => (
                    <td
                      key={event}
                      style={{...styles.tableCell, textAlign: 'right', cursor: 'pointer'}}
                      onClick={() => setSelectedLP(stat.landing_page_id)}
                    >
                      {((stat as any)[`event_${event}`] || 0).toLocaleString()}
                    </td>
                  ))}
                  <td
                    style={{...styles.tableCell, fontSize: 12, color: '#666', cursor: 'pointer'}}
                    onClick={() => setSelectedLP(stat.landing_page_id)}
                  >
                    {new Date(stat.last_click).toLocaleString()}
                  </td>
                  <td style={{...styles.tableCell, borderRight: 'none'}}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        loadLPForEdit(stat.landing_page_id)
                      }}
                      style={styles.iconBtn}
                      title="Edit landing page"
                    >
                      ✏️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>Edit Landing Page</h3>

            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Landing Page UUID (immutable):</div>
              <code style={{ fontSize: 13 }}>{editing.id}</code>
            </div>

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="VSL Landing Page"
              />
            </label>
            <label style={styles.label}>
              URL *<br/>
              <input
                style={styles.input}
                value={editing.url}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                placeholder="https://lp.flow-trk.com/vsl-1"
              />
            </label>
            <label style={styles.label}>
              Description<br/>
              <textarea
                style={{...styles.input, minHeight: 80}}
                value={editing.description || ''}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Optional description"
              />
            </label>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={saveLP} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Landing Page Drill-Down View
type LandingPageDetail = {
  landing_page_id: string
  summary: {
    total_clicks: number
    unique_visitors: number
    first_click: string
    last_click: string
  }
  campaigns: Array<{ campaign_id: string; campaign_name: string; clicks: number; unique_visitors: number }>
}

function LandingPageDetailView({ lpId, onBack, setError }: { lpId: string; onBack: () => void; setError: (e: string | null) => void }) {
  const [detail, setDetail] = useState<LandingPageDetail | null>(null)
  const [loading, setLoading] = useState(false)

  async function loadDetail() {
    try {
      setLoading(true); setError(null)
      const res = await api(`/stats/landing-page/${encodeURIComponent(lpId)}`)
      setDetail(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadDetail() }, [])

  return (
    <div>
      <button onClick={onBack} style={{ ...styles.btn, marginBottom: 16 }}>← Back to Landing Page List</button>

      <h2 style={{ margin: '0 0 16px 0' }}>{lpId}</h2>

      {detail && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Total Clicks</div>
            <div style={styles.summaryValue}>{detail.summary.total_clicks?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Unique Visitors</div>
            <div style={styles.summaryValue}>{detail.summary.unique_visitors?.toLocaleString() || 0}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>First Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.first_click ? new Date(detail.summary.first_click).toLocaleDateString() : 'N/A'}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Last Click</div>
            <div style={{...styles.summaryValue, fontSize: 14}}>{detail.summary.last_click ? new Date(detail.summary.last_click).toLocaleDateString() : 'N/A'}</div>
          </div>
        </div>
      )}

      <h3>Campaigns Using This Landing Page</h3>

      {loading && <div>Loading...</div>}

      {detail && detail.campaigns.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Campaign Name</th>
              <th style={styles.tableHeader}>Campaign ID</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Clicks</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Unique Visitors</th>
            </tr>
          </thead>
          <tbody>
            {detail.campaigns.map((row, i) => (
              <tr key={i} style={styles.tableRow}>
                <td style={styles.tableCell}>{row.campaign_name}</td>
                <td style={{...styles.tableCell, fontFamily: 'monospace', fontSize: 12}}>{row.campaign_id}</td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>{row.clicks.toLocaleString()}</td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>{row.unique_visitors.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && detail.campaigns.length === 0 && !loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No campaigns using this landing page yet
        </div>
      )}
    </div>
  )
}

// ===== ADMIN: CAMPAIGNS TAB =====
function CampaignsTab({ setError }: { setError: (e: string | null) => void }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [flows, setFlows] = useState<Flow[]>([])
  const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([])
  const [editing, setEditing] = useState<Campaign | null>(null)
  const [loading, setLoading] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  async function loadCampaigns() {
    try {
      setLoading(true); setError(null)
      const res = await api('/campaigns')
      setCampaigns(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function loadFlows() {
    try {
      const res = await api('/flows')
      setFlows(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function loadTrafficSources() {
    try {
      const res = await api('/traffic-sources')
      setTrafficSources(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function save() {
    if (!editing) return
    try {
      setError(null)

      // Generate UUID if new
      if (!editing.id) {
        editing.id = generateUUID()
      }

      if (!editing.host || !editing.flow_id) {
        throw new Error('Host and Flow are required')
      }

      await api(`/campaigns/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await loadCampaigns()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function del(campaign: Campaign) {
    if (!confirm(`Delete campaign "${campaign.name || campaign.id}"?`)) return
    try {
      setError(null)
      await api(`/campaigns/${encodeURIComponent(campaign.id)}?host=${encodeURIComponent(campaign.host)}`, { method: 'DELETE' })
      await loadCampaigns()
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function copyLink(campaign: Campaign) {
    let url = `https://${campaign.host}/${campaign.id}`

    // Add traffic source parameters if configured
    if (campaign.traffic_source_id) {
      const ts = trafficSources.find(t => t.id === campaign.traffic_source_id)
      if (ts && ts.params && ts.params.length > 0) {
        const macroParams = ts.params.map(p => `${p.param_name}=${p.macro}`).join('&')
        url = `${url}?${macroParams}`
      }
    }

    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(campaign.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (e) {
      setError('Failed to copy to clipboard')
    }
  }

  useEffect(() => { loadCampaigns(); loadFlows(); loadTrafficSources() }, [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Campaigns</h2>
        <button
          onClick={() => setEditing({ id: '', host: 'trk.flow-trk.com', name: '', flow_id: '', active: true })}
          style={styles.btnPrimary}
        >
          + New Campaign
        </button>
      </div>

      {loading && <div>Loading...</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {campaigns.map(campaign => (
          <div key={`${campaign.host}:${campaign.id}`} style={styles.card}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontWeight: 'bold' }}>{campaign.name || 'Unnamed Campaign'}</span>
                {campaign.active ? (
                  <span style={{ fontSize: 11, background: '#0a0', color: 'white', padding: '2px 6px', borderRadius: 4 }}>ACTIVE</span>
                ) : (
                  <span style={{ fontSize: 11, background: '#999', color: 'white', padding: '2px 6px', borderRadius: 4 }}>INACTIVE</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#666' }}>Flow: {campaign.flow_id}</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontFamily: 'monospace' }}>
                https://{campaign.host}/{campaign.id}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => copyLink(campaign)}
                style={copiedId === campaign.id ? styles.btnSuccess : styles.btnPrimary}
              >
                {copiedId === campaign.id ? 'Copied!' : 'Copy Link'}
              </button>
              <button onClick={() => setEditing(campaign)} style={styles.btn}>Edit</button>
              <button onClick={() => del(campaign)} style={styles.btnDanger}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit Campaign' : 'New Campaign'}</h3>

            {editing.id && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Campaign UUID (immutable):</div>
                <code style={{ fontSize: 13 }}>{editing.id}</code>
              </div>
            )}

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Summer 2025 Campaign"
              />
            </label>
            <label style={styles.label}>
              Host *<br/>
              <input
                style={styles.input}
                value={editing.host}
                onChange={(e) => setEditing({ ...editing, host: e.target.value })}
                placeholder="trk.flow-trk.com"
              />
            </label>
            <label style={styles.label}>
              Flow *<br/>
              <select
                style={styles.input}
                value={editing.flow_id}
                onChange={(e) => setEditing({ ...editing, flow_id: e.target.value })}
              >
                <option value="">Select flow...</option>
                {flows.map(f => <option key={f.id} value={f.id}>{f.name || f.id}</option>)}
              </select>
            </label>
            <label style={styles.label}>
              Traffic Source (optional)<br/>
              <select
                style={styles.input}
                value={editing.traffic_source_id || ''}
                onChange={(e) => setEditing({ ...editing, traffic_source_id: e.target.value || undefined })}
              >
                <option value="">None</option>
                {trafficSources.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
              </select>
            </label>
            <div style={{ fontSize: 12, color: '#666', padding: 8, background: '#f0f8ff', borderRadius: 6, marginBottom: 12 }}>
              💡 Traffic sources define how URL parameters are mapped to tracking columns (e.g., gclid → externalid1)
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              Active
            </label>

            {editing.host && (
              <div style={{ marginTop: 16, padding: 12, background: '#e8f5e9', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Base Tracking URL:</div>
                <code style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                  https://{editing.host}/{editing.id || '{uuid-will-be-generated}'}
                </code>
                {editing.traffic_source_id && (() => {
                  const ts = trafficSources.find(t => t.id === editing.traffic_source_id)
                  if (ts && ts.params && ts.params.length > 0) {
                    const macroParams = ts.params.map(p => `${p.param_name}=${p.macro}`).join('&')
                    return (
                      <>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 12, marginBottom: 4 }}>
                          Full URL with {ts.name} Parameters:
                        </div>
                        <code style={{ fontSize: 13, display: 'block', wordBreak: 'break-all' }}>
                          https://{editing.host}/{editing.id || '{uuid-will-be-generated}'}?{macroParams}
                        </code>
                      </>
                    )
                  }
                  return null
                })()}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ===== ADMIN: FLOWS TAB =====
function FlowsTab({ setError }: { setError: (e: string | null) => void }) {
  const [flows, setFlows] = useState<Flow[]>([])
  const [offers, setOffers] = useState<Offer[]>([])
  const [landingPages, setLandingPages] = useState<LandingPage[]>([])
  const [editing, setEditing] = useState<Flow | null>(null)
  const [loading, setLoading] = useState(false)

  async function loadFlows() {
    try {
      setLoading(true); setError(null)
      const res = await api('/flows')
      setFlows(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function loadOffers() {
    try {
      const res = await api('/offers')
      setOffers(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function loadLandingPages() {
    try {
      const res = await api('/landing-pages')
      setLandingPages(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function save() {
    if (!editing) return
    try {
      setError(null)

      // Generate UUID if new
      if (!editing.id) {
        editing.id = generateUUID()
      }

      if (!editing.variants || editing.variants.length === 0) {
        throw new Error('At least one variant is required')
      }

      await api(`/flows/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await loadFlows()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function del(id: string) {
    if (!confirm(`Delete flow "${id}"?`)) return
    try {
      setError(null)
      await api(`/flows/${encodeURIComponent(id)}`, { method: 'DELETE' })
      await loadFlows()
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { loadFlows(); loadOffers(); loadLandingPages() }, [])

  const totalWeight = editing ? editing.variants.reduce((s, v) => s + (v.weight || 0), 0) : 0

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Flows</h2>
        <button onClick={() => setEditing({ id: '', name: '', use_landing_pages: false, variants: [] })} style={styles.btnPrimary}>+ New Flow</button>
      </div>

      {loading && <div>Loading...</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {flows.map(flow => (
          <div key={flow.id} style={styles.card}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{flow.name || 'Unnamed Flow'}</div>
              <div style={{ fontSize: 13, color: '#666' }}>
                {flow.variants.length} variant(s): {flow.variants.map(v => `${v.offer_id} (${v.weight})`).join(', ')}
              </div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontFamily: 'monospace' }}>UUID: {flow.id}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(flow)} style={styles.btn}>Edit</button>
              <button onClick={() => del(flow.id)} style={styles.btnDanger}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit Flow' : 'New Flow'}</h3>

            {editing.id && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Flow UUID (immutable):</div>
                <code style={{ fontSize: 13 }}>{editing.id}</code>
              </div>
            )}

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="My Flow"
              />
            </label>

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={editing.use_landing_pages || false}
                onChange={(e) => setEditing({ ...editing, use_landing_pages: e.target.checked })}
              />
              Use Landing Pages
            </label>

            {editing.use_landing_pages && (
              <div style={{ fontSize: 12, color: '#666', marginBottom: 16, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
                💡 When enabled, users will be sent to a landing page first, then redirected to the offer when they click the CTA button
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>Variants (Total weight: {totalWeight})</strong>
                <button
                  onClick={() => setEditing({
                    ...editing,
                    variants: [...editing.variants, {
                      landing_page_id: editing.use_landing_pages ? '' : undefined,
                      offer_id: '',
                      weight: 100
                    }]
                  })}
                  style={styles.btnSmall}
                >
                  + Add Variant
                </button>
              </div>
              {editing.variants.map((v, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: editing.use_landing_pages ? '1fr 1fr 100px 80px' : '1fr 100px 80px', gap: 8, marginBottom: 8 }}>
                  {editing.use_landing_pages && (
                    <select
                      style={styles.input}
                      value={v.landing_page_id || ''}
                      onChange={(e) => {
                        const vs = [...editing.variants]
                        vs[i] = { ...v, landing_page_id: e.target.value }
                        setEditing({ ...editing, variants: vs })
                      }}
                    >
                      <option value="">Select landing page...</option>
                      {landingPages.map(lp => <option key={lp.id} value={lp.id}>{lp.name || lp.id}</option>)}
                    </select>
                  )}
                  <select
                    style={styles.input}
                    value={v.offer_id}
                    onChange={(e) => {
                      const vs = [...editing.variants]
                      vs[i] = { ...v, offer_id: e.target.value }
                      setEditing({ ...editing, variants: vs })
                    }}
                  >
                    <option value="">Select offer...</option>
                    {offers.map(o => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
                  </select>
                  <input
                    style={styles.input}
                    type="number"
                    value={v.weight}
                    onChange={(e) => {
                      const vs = [...editing.variants]
                      vs[i] = { ...v, weight: Number(e.target.value) }
                      setEditing({ ...editing, variants: vs })
                    }}
                  />
                  <button
                    onClick={() => {
                      const vs = [...editing.variants]
                      vs.splice(i, 1)
                      setEditing({ ...editing, variants: vs })
                    }}
                    style={styles.btnSmall}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ===== ADMIN: OFFERS TAB =====
function OffersTab({ setError }: { setError: (e: string | null) => void }) {
  const [offers, setOffers] = useState<Offer[]>([])
  const [editing, setEditing] = useState<Offer | null>(null)
  const [loading, setLoading] = useState(false)

  async function load() {
    try {
      setLoading(true); setError(null)
      const res = await api('/offers')
      setOffers(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function save() {
    if (!editing) return
    try {
      setError(null)

      // Generate UUID if new
      if (!editing.id) {
        editing.id = generateUUID()
      }

      if (!editing.url) throw new Error('URL is required')

      await api(`/offers/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await load()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function del(id: string) {
    if (!confirm(`Delete offer "${id}"?`)) return
    try {
      setError(null)
      await api(`/offers/${encodeURIComponent(id)}`, { method: 'DELETE' })
      await load()
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { load() }, [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Offers</h2>
        <button onClick={() => setEditing({ id: '', name: '', url: '' })} style={styles.btnPrimary}>+ New Offer</button>
      </div>

      {loading && <div>Loading...</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {offers.map(offer => (
          <div key={offer.id} style={styles.card}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{offer.name || 'Unnamed Offer'}</div>
              <div style={{ fontSize: 13, color: '#666', wordBreak: 'break-all' }}>{offer.url}</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontFamily: 'monospace' }}>UUID: {offer.id}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(offer)} style={styles.btn}>Edit</button>
              <button onClick={() => del(offer.id)} style={styles.btnDanger}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit Offer' : 'New Offer'}</h3>

            {editing.id && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Offer UUID (immutable):</div>
                <code style={{ fontSize: 13 }}>{editing.id}</code>
              </div>
            )}

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="GitHub Landing Page"
              />
            </label>
            <label style={styles.label}>
              URL * (use {'{clickid}'} macro)<br/>
              <input
                style={styles.input}
                value={editing.url}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                placeholder="https://example.com/lp?clickid={clickid}"
              />
            </label>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
              💡 Tip: Use {'{clickid}'} in your URL to track conversions back to this click
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ===== ADMIN: LANDING PAGES TAB =====
function LandingPagesTab({ setError }: { setError: (e: string | null) => void }) {
  const [landingPages, setLandingPages] = useState<LandingPage[]>([])
  const [editing, setEditing] = useState<LandingPage | null>(null)
  const [loading, setLoading] = useState(false)

  async function load() {
    try {
      setLoading(true); setError(null)
      const res = await api('/landing-pages')
      setLandingPages(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function save() {
    if (!editing) return
    try {
      setError(null)

      // Generate UUID if new
      if (!editing.id) {
        editing.id = generateUUID()
      }

      if (!editing.name || !editing.url) throw new Error('Name and URL are required')

      await api(`/landing-pages/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(editing) })
      await load()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function del(id: string) {
    if (!confirm(`Delete landing page "${id}"?`)) return
    try {
      setError(null)
      await api(`/landing-pages/${encodeURIComponent(id)}`, { method: 'DELETE' })
      await load()
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { load() }, [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Landing Pages</h2>
        <button onClick={() => setEditing({ id: '', name: '', url: '', description: '' })} style={styles.btnPrimary}>+ New Landing Page</button>
      </div>

      {loading && <div>Loading...</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {landingPages.map(lp => (
          <div key={lp.id} style={styles.card}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{lp.name || 'Unnamed Landing Page'}</div>
              {lp.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{lp.description}</div>}
              <div style={{ fontSize: 13, color: '#666', wordBreak: 'break-all' }}>{lp.url}</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontFamily: 'monospace' }}>UUID: {lp.id}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(lp)} style={styles.btn}>Edit</button>
              <button onClick={() => del(lp.id)} style={styles.btnDanger}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit Landing Page' : 'New Landing Page'}</h3>

            {editing.id && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Landing Page UUID (immutable):</div>
                <code style={{ fontSize: 13 }}>{editing.id}</code>
              </div>
            )}

            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="VSL Landing Page"
              />
            </label>
            <label style={styles.label}>
              URL *<br/>
              <input
                style={styles.input}
                value={editing.url}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                placeholder="https://lp.flow-trk.com/vsl-1"
              />
            </label>
            <label style={styles.label}>
              Description<br/>
              <textarea
                style={{...styles.input, minHeight: 80}}
                value={editing.description || ''}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Optional description of this landing page"
              />
            </label>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
              💡 Tip: The system will automatically append ?click_id=xxx to this URL when redirecting users
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ===== ADMIN: CUSTOM EVENTS TAB =====
function TrafficSourcesTab({ setError }: { setError: (e: string | null) => void }) {
  const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([])
  const [editing, setEditing] = useState<TrafficSource | null>(null)
  const [loading, setLoading] = useState(false)

  const validFields = [
    'var1', 'var2', 'var3', 'var4', 'var5', 'var6', 'var7', 'var8', 'var9', 'var10',
    'var11', 'var12', 'var13', 'var14', 'var15', 'var16', 'var17', 'var18', 'var19', 'var20'
  ]

  async function load() {
    try {
      setLoading(true); setError(null)
      const res = await api('/traffic-sources')
      setTrafficSources(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function save() {
    if (!editing) return
    try {
      setError(null)

      if (!editing.id || !editing.name) throw new Error('ID and Name are required')
      if (!editing.params || editing.params.length === 0) throw new Error('At least one parameter mapping is required')

      // Validate params
      for (const param of editing.params) {
        if (!param.name || !param.param_name || !param.target_field || !param.macro) {
          throw new Error('All parameters must have name, param_name, target_field, and macro')
        }
        if (!validFields.includes(param.target_field)) {
          throw new Error(`Invalid target_field: ${param.target_field}`)
        }
      }

      await api(`/traffic-sources/${encodeURIComponent(editing.id)}`, {
        method: 'PUT',
        body: JSON.stringify(editing)
      })
      await load()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function del(id: string) {
    if (!confirm(`Delete traffic source "${id}"?`)) return
    try {
      setError(null)
      await api(`/traffic-sources/${encodeURIComponent(id)}`, { method: 'DELETE' })
      await load()
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  function addParam() {
    if (!editing) return
    setEditing({
      ...editing,
      params: [...editing.params, { name: '', param_name: '', target_field: 'var1', macro: '' }]
    })
  }

  function removeParam(index: number) {
    if (!editing) return
    setEditing({
      ...editing,
      params: editing.params.filter((_, i) => i !== index)
    })
  }

  function updateParam(index: number, field: 'name' | 'param_name' | 'target_field' | 'macro', value: string) {
    if (!editing) return
    setEditing({
      ...editing,
      params: editing.params.map((p, i) => i === index ? { ...p, [field]: value } : p)
    })
  }

  useEffect(() => { load() }, [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Traffic Sources</h2>
        <button onClick={() => setEditing({ id: '', name: '', params: [] })} style={styles.btnPrimary}>+ New Traffic Source</button>
      </div>

      {loading && <div>Loading...</div>}

      <div style={{ marginBottom: 16, padding: 12, background: '#f0f8ff', borderRadius: 8, fontSize: 13 }}>
        <strong>Traffic Sources</strong> define how URL parameters from ad platforms (like Google Ads, Facebook, TikTok) are mapped to standardized tracking columns.
        <br/>
        Map URL parameters (e.g., gclid, campaignid) to target fields (var1-var20) with macros for easy URL building.
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {trafficSources.map(ts => (
          <div key={ts.id} style={styles.card}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{ts.name}</div>
              <div style={{ fontSize: 11, color: '#999', fontFamily: 'monospace', marginBottom: 8 }}>ID: {ts.id}</div>
              <div style={{ fontSize: 13, color: '#666' }}>
                {ts.params.length} parameter mapping{ts.params.length !== 1 ? 's' : ''}
                {ts.params.length > 0 && (
                  <span style={{ marginLeft: 8 }}>
                    ({ts.params.slice(0, 3).map(p => p.param_name).join(', ')}
                    {ts.params.length > 3 && '...'})
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(ts)} style={styles.btn}>Edit</button>
              <button onClick={() => del(ts.id)} style={styles.btnDanger}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: 800 }}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit Traffic Source' : 'New Traffic Source'}</h3>

            <label style={styles.label}>
              ID * (lowercase, use hyphens)<br/>
              <input
                style={styles.input}
                value={editing.id}
                onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                placeholder="google-ads-search"
              />
            </label>
            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Google Ads - Search/Display"
              />
            </label>

            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong>Parameter Mappings</strong>
                <button onClick={addParam} style={styles.btn}>+ Add Parameter</button>
              </div>

              {editing.params.length === 0 && (
                <div style={{ padding: 16, background: '#fef3c7', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                  No parameters defined yet. Click "+ Add Parameter" to start mapping URL parameters.
                </div>
              )}

              <div style={{ display: 'grid', gap: 8 }}>
                {editing.params.map((param, i) => (
                  <div key={i} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
                    gap: 8,
                    padding: 12,
                    background: '#f8fafc',
                    borderRadius: 8,
                    alignItems: 'end'
                  }}>
                    <label style={{ ...styles.label, marginBottom: 0 }}>
                      Name<br/>
                      <input
                        style={styles.input}
                        value={param.name}
                        onChange={(e) => updateParam(i, 'name', e.target.value)}
                        placeholder="e.g., Campaign ID"
                      />
                    </label>
                    <label style={{ ...styles.label, marginBottom: 0 }}>
                      URL Parameter<br/>
                      <input
                        style={styles.input}
                        value={param.param_name}
                        onChange={(e) => updateParam(i, 'param_name', e.target.value)}
                        placeholder="e.g., campaignid"
                      />
                    </label>
                    <label style={{ ...styles.label, marginBottom: 0 }}>
                      Maps To<br/>
                      <select
                        style={styles.input}
                        value={param.target_field}
                        onChange={(e) => updateParam(i, 'target_field', e.target.value)}
                      >
                        {Array.from({ length: 20 }, (_, i) => (
                          <option key={i} value={`var${i + 1}`}>var{i + 1}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ ...styles.label, marginBottom: 0 }}>
                      Macro<br/>
                      <input
                        style={styles.input}
                        value={param.macro}
                        onChange={(e) => updateParam(i, 'macro', e.target.value)}
                        placeholder="e.g., {campaignid}"
                      />
                    </label>
                    <button onClick={() => removeParam(i)} style={{ ...styles.btnDanger, padding: '10px 16px' }}>Remove</button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ fontSize: 12, color: '#666', marginTop: 16, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
              💡 Example: Name: "Campaign ID", URL Param: "campaignid", Maps To: "var1", Macro: "{'{'} campaignid {'}'}"
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CustomEventsTab({ setError }: { setError: (e: string | null) => void }) {
  const [events, setEvents] = useState<CustomEvent[]>([])
  const [editing, setEditing] = useState<CustomEvent | null>(null)
  const [loading, setLoading] = useState(false)

  async function load() {
    try {
      setLoading(true); setError(null)
      const res = await api('/custom-events')
      setEvents(await res.json())
    } catch (e: any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function save() {
    if (!editing) return
    try {
      setError(null)

      if (!editing.id || !editing.name) throw new Error('ID and Name are required')

      // Convert name to ID format (lowercase, no spaces)
      const eventId = editing.id.toLowerCase().replace(/\s+/g, '_')

      await api(`/custom-events/${encodeURIComponent(eventId)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...editing, id: eventId })
      })
      await load()
      setEditing(null)
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  async function del(id: string) {
    if (!confirm(`Delete event "${id}"?`)) return
    try {
      setError(null)
      await api(`/custom-events/${encodeURIComponent(id)}`, { method: 'DELETE' })
      await load()
    } catch (e: any) {
      setError(e.message || String(e))
    }
  }

  useEffect(() => { load() }, [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Custom Events</h2>
        <button onClick={() => setEditing({ id: '', name: '', description: '' })} style={styles.btnPrimary}>+ New Event</button>
      </div>

      {loading && <div>Loading...</div>}

      <div style={{ marginBottom: 16, padding: 12, background: '#f0f8ff', borderRadius: 8, fontSize: 13 }}>
        <strong>Built-in Events:</strong> impression, lp_click, conversion
        <br/>
        <strong>Custom Events:</strong> Track any event via POST /event (e.g. quiz_started, lead_submitted, purchase)
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {events.map(event => (
          <div key={event.id} style={styles.card}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{event.name}</div>
              {event.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{event.description}</div>}
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontFamily: 'monospace' }}>Event ID: {event.id}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(event)} style={styles.btn}>Edit</button>
              <button onClick={() => del(event.id)} style={styles.btnDanger}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit Custom Event' : 'New Custom Event'}</h3>

            <label style={styles.label}>
              Event ID * (lowercase, use underscores)<br/>
              <input
                style={styles.input}
                value={editing.id}
                onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                placeholder="quiz_started"
                disabled={!!editing.id && events.some(ev => ev.id === editing.id)}
              />
            </label>
            <label style={styles.label}>
              Name *<br/>
              <input
                style={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Quiz Started"
              />
            </label>
            <label style={styles.label}>
              Description<br/>
              <textarea
                style={{...styles.input, minHeight: 80}}
                value={editing.description || ''}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Optional description of when this event fires"
              />
            </label>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
              💡 Tip: Track this event via POST /event with {"{ click_id, event_type: '"}{ editing.id || 'event_id' }{"' }"}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} style={styles.btnPrimary}>Save</button>
              <button onClick={() => setEditing(null)} style={styles.btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Styles
const styles = {
  card: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'white',
    border: 'none',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease',
  },
  btn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    background: 'white',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    transition: 'all 0.2s ease',
  },
  btnPrimary: {
    padding: '10px 24px',
    borderRadius: 8,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600 as const,
    fontSize: 14,
    boxShadow: '0 1px 3px rgba(37, 99, 235, 0.3)',
    transition: 'all 0.2s ease',
  },
  btnSuccess: {
    padding: '10px 24px',
    borderRadius: 8,
    border: 'none',
    background: '#10b981',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600 as const,
    fontSize: 14,
    boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)',
  },
  btnDanger: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    background: '#dc2626',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
    boxShadow: '0 1px 3px rgba(220, 38, 38, 0.3)',
    transition: 'all 0.2s ease',
  },
  btnSmall: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #e0e0e0',
    background: 'white',
    cursor: 'pointer',
    fontSize: 13,
    transition: 'all 0.2s ease',
  },
  iconBtn: {
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 16,
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: 'white',
    borderRadius: 16,
    padding: 32,
    maxWidth: 600,
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  label: {
    display: 'block',
    marginBottom: 16,
    fontSize: 14,
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 8,
    border: '2px solid #e0e0e0',
    marginTop: 6,
    fontSize: 14,
    transition: 'border-color 0.2s ease',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: 'white',
    border: 'none',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  tableHeaderRow: {
    background: '#1e293b',
    color: 'white',
  },
  tableHeader: {
    padding: 16,
    textAlign: 'left' as const,
    fontWeight: 600 as const,
    fontSize: 13,
    color: 'white',
    borderRight: '1px solid #334155',
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer' as const,
    transition: 'all 0.2s ease',
    background: 'white',
  },
  tableCell: {
    padding: 16,
    borderRight: '1px solid #e5e7eb',
  },
  summaryCard: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 20,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  summaryLabel: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 8,
    fontWeight: 500,
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 700 as const,
    color: '#1e293b',
  },
}
